import React, { useState } from 'react'
import styled from 'styled-components'
import { useMatchBreakpoints } from 'toonswap-uikit'
import { useTranslation } from 'contexts/Localization'
import { Pool } from 'state/types'

import PoolCell, { PoolCellProps } from './PoolCell'
import Earned, { EarnedProps } from './Earned'
import PoolApr from './PoolApr'
import Staked, { StakedProps } from './Staked'
import EndTime, { EndTimeProps } from './EndTime'
import Details from './Details'
import CellLayout from './CellLayout'
import PoolActionPanel from './Actions/PoolActionPanel'
import { DesktopColumnSchema, MobileColumnSchema } from './types'

export interface RowProps {
  pool: PoolCellProps
  earned: EarnedProps
  apr?: Pool // present so useTable's sortDataInOrder finds column 'apr'; APR is rendered from details in PoolApr
  staked: StakedProps
  endTime: EndTimeProps
  details: Pool
}

const cells = {
  pool: PoolCell,
  earned: Earned,
  staked: Staked,
  endTime: EndTime,
}

const CellInner = styled.div`
  padding: 18px 0px;
  display: flex;
  width: 100%;
  align-items: center;
  padding-right: 8px;

  ${({ theme }) => theme.mediaQueries.xl} {
    padding-right: 32px;
  }
`

const StyledTr = styled.tr`
  cursor: pointer;
  position: relative;
  z-index: 1;
  background: #475355;
  border: 3px solid #252c2d;

  & td {
    background: #475355;
    border-top: 3px solid #252c2d;
    border-bottom: 3px solid #252c2d;
    border-left: none;
    border-right: none;
  }

  & td:first-child {
    border-left: 3px solid #252c2d;
    border-radius: 18px 0 0 18px;
  }

  & td:last-child {
    border-right: 3px solid #252c2d;
    border-radius: 0 18px 18px 0;
  }

  &:hover td {
    background: #505c5e;
    border-color: #252c2d;
  }
`

const EarnedMobileCell = styled.td`
  padding: 16px 0 24px 16px;
`

const AprMobileCell = styled.td`
  padding-top: 16px;
  padding-bottom: 24px;
`

const EndTimeMobileCell = styled.td`
  padding-top: 16px;
  padding-bottom: 24px;
`

const PoolMobileCell = styled.td`
  padding-top: 24px;
`

const ExpandedTd = styled.td`
  padding: 0;
  vertical-align: top;
  border: none;
`

const ExpandedWrapper = styled.div`
  margin-top: -28px;
  position: relative;
  z-index: 0;
`

const Row: React.FunctionComponent<RowProps> = (props) => {
  const { details } = props
  const [actionPanelToggled, setActionPanelToggled] = useState(false)
  const { t } = useTranslation()
  const { isXl, isXs } = useMatchBreakpoints()
  const isMobile = !isXl
  const tableSchema = isMobile ? MobileColumnSchema : DesktopColumnSchema
  const columnNames = tableSchema.map((column) => column.name)

  const handleRenderRow = () => {
    if (!isXs) {
      return (
        <StyledTr onClick={() => setActionPanelToggled(!actionPanelToggled)}>
          {columnNames.map((key) => {
            switch (key) {
              case 'details':
                return (
                  <td key={key}>
                    <CellInner>
                      <CellLayout>
                        <Details actionPanelToggled={actionPanelToggled} />
                      </CellLayout>
                    </CellInner>
                  </td>
                )
              case 'apr':
                return (
                  <td key={key}>
                    <CellInner>
                      <CellLayout label={t('APR')}>
                        <PoolApr pool={details} />
                      </CellLayout>
                    </CellInner>
                  </td>
                )
              default:
                return (
                  <td key={key}>
                    <CellInner>
                      <CellLayout label={t(tableSchema.find((c) => c.name === key)?.label || '')}>
                        {React.createElement(cells[key], props[key])}
                      </CellLayout>
                    </CellInner>
                  </td>
                )
            }
          })}
        </StyledTr>
      )
    }

    return (
      <StyledTr onClick={() => setActionPanelToggled(!actionPanelToggled)}>
        <td>
          <tr>
            <PoolMobileCell>
              <CellLayout>
                <PoolCell {...props.pool} />
              </CellLayout>
            </PoolMobileCell>
          </tr>
          <tr style={{ display: 'flex', justifyContent: 'space-evenly' }}>
            <EarnedMobileCell>
              <CellLayout label={t('Earned')}>
                <Earned {...props.earned} />
              </CellLayout>
            </EarnedMobileCell>
            <AprMobileCell>
              <CellLayout label={t('APR')}>
                <PoolApr pool={details} />
              </CellLayout>
            </AprMobileCell>
            <AprMobileCell>
              <CellLayout label={t('Your Staked')}>
                <Staked {...props.staked} />
              </CellLayout>
            </AprMobileCell>
            <EndTimeMobileCell>
              <CellLayout label={t('End Time')}>
                <EndTime {...props.endTime} />
              </CellLayout>
            </EndTimeMobileCell>
          </tr>
        </td>
        <td>
          <CellInner>
            <CellLayout>
              <Details actionPanelToggled={actionPanelToggled} />
            </CellLayout>
          </CellInner>
        </td>
      </StyledTr>
    )
  }

  return (
    <>
      {handleRenderRow()}
      {actionPanelToggled && details && (
        <tr>
          <ExpandedTd colSpan={isXs ? 2 : tableSchema.length}>
            <ExpandedWrapper>
              <PoolActionPanel pool={details} />
            </ExpandedWrapper>
          </ExpandedTd>
        </tr>
      )}
    </>
  )
}

export default Row
